const bigjs = require('big.js');

/**
 * getMonthlyPayment - Returns the payment amount per month
 *
 * @param {number} amount    Loan amount
 * @param {number} months    Duration of loan in months
 * @param {number} interest  Interest rate of the loan
 */
export function getMonthlyPayment(amount, months, interest) {
  return roundToCent(amount * interest / 12 / (1 - Math.pow(interest / 12 + 1, months * -1)));
}

/**
 * getMonthlyInterestPayment (IPMT in excel) - Returns the interest for a given month
 * https://gist.github.com/econ-r/dcd503815bbb271484ff#file-excelfinance-r-L55
 *
 * @param {number} amount        Loan amount
 * @param {number} months        Duration of loan in months
 * @param {number} interest      Interest rate of the loan
 * @param {number} currentMonth  month who's interest should be counted
 */
export function getMonthlyInterestPayment(amount, months, interest, currentMonth) {
  const paymentAmount = getMonthlyPayment(amount, months, interest) * -1;
  return roundToCent(
    Math.pow(interest / 12 + 1, currentMonth - 1) * (amount * interest / 12 + paymentAmount) -
    paymentAmount
  );
}

/**
 * getTotalInterestPayment - Returns the sum of all interests
 *
 * @param {number} amount         Loan amount
 * @param {number} months         Duration of loan in months
 * @param {number} interest       Interest rate of the loan
 */
export function getTotalInterestPayment(amount, months, interest) {
  return roundToCent(
    Array.from(Array(months))
      .map((_, idx) => getMonthlyInterestPayment(amount, months, interest, idx + 1))
      .reduce((i, interestTotal) => i + interestTotal, 0)
  );
}

/**
 * getPaymentTotal - Returns the total payment including interest
 *
 * @param {number} amount         Loan amount
 * @param {number} months         Duration of loan in months
 * @param {number} interest       Interest rate of the loan
 */
export function getPaymentTotal(amount, months, interest) {
  return roundToCent(amount + getTotalInterestPayment(amount, months, interest));
}

function roundToCent(amount) {
  return parseFloat(Number(amount).toFixed(2));
}

/**
 * getRpmn - Returns the rpmn value based on params
 * https://www.mbank.sk/blog/post,600,viete-co-je-to-rpmn.html
 *
 * @param {number} loanAmount       Loan amount
 * @param {number} payment          One time payment of loan
 * @param {number} count            Count of payments in moths
 */
export function getRpmn(loanAmount, payment, count) {
  let halfLow = 0;
  let halfHigh = 1;
  let rpmn;
  let sum;
  let iterations = 0;
  const MAX_ITERATIONS = 1000;

  do {
    sum = 0;
    rpmn = halfLow + (halfHigh - halfLow) / 2;
    for (let k = 1; k <= count; k++) {
      sum += payment / Math.pow(1 + rpmn, k / 12);
    }
    if (sum > loanAmount) {
      halfLow = rpmn;
    } else {
      halfHigh = rpmn;
    }
    iterations++;
  } while (Math.abs(sum - loanAmount) > 0.000001 && iterations < MAX_ITERATIONS);

  return rpmn;
}

/**
 * getProvisionFee - Returns the provision fee based on amount and number of loans
 *
 * @param {number} amount               Loan amount
 * @param {number} provisionFees        array provision fees
 */
export function getProvisionFee(amount, provisionFees, loans) {
  let provisionFee;
  if (loans) {
    if (loans.length > 1) {
      provisionFee = provisionFees[2];
    }
    else if (loans.length === 1) {
      provisionFee = provisionFees[1];
    } else {
      provisionFee = provisionFees[0];
    }
  } else provisionFee = provisionFees[0];
  let provision = (amount * (provisionFee / 100)) < 20 ? 20 : (amount * (provisionFee / 100));
  return roundToCent(provision);
}

/**
 * getPrincipalBalance - Returns the principal balance based on unpaid investors installments
 * zostatok istiny
 * @param {number} amount                 Loan amount
 * @param {number} investments            investments of loan
 */
export function getPrincipalBalance(loan, penaltyFees) {
  let leftAmount = bigjs(0);

  for (let investment of loan.investments) {
    let sumOfInvestmentPrincipal = investment.installments.reduce((total, installment) => {
      return Number(total + Number(installment.principalUnpaid))
    }, 0)
    leftAmount = leftAmount.plus(bigjs(sumOfInvestmentPrincipal))
  }
  
  return leftAmount.toFixed(2);
}

/**
 * getNominalBalance - Returns the principal balance base on left amount and payed installments
 * menovita hodnota
 * @param {number} amount                 Loan amount
 * @param {number} installments           installments of loan
 */
export function getNominalBalance(loan, penaltyFees) {
  let leftAmount = bigjs(loan.amount);
  let shouldPaid = bigjs(0);
  let realPaid = bigjs(0);
  const mappedInstallments = loan.installments.map( item => {
    const {
      paymentDue, 
      interestAmount, 
      amount,
      insuranceUnpaid,
      insuranceAmount,
    } = item;
    return {
      paymentDue: new Date(paymentDue),
      interestAmount: bigjs(interestAmount),
      amount: bigjs(amount),
      insuranceUnpaid: insuranceUnpaid ? bigjs(insuranceUnpaid) : bigjs(0),
      insuranceAmount: insuranceAmount ? bigjs(insuranceAmount) : bigjs(0),
    };
  })
  mappedInstallments.filter(i => i.paymentDue <= new Date()).forEach(installment => {
    leftAmount = leftAmount.plus(installment.interestAmount).plus(installment.insuranceAmount);
    shouldPaid = shouldPaid.plus(installment.amount);
  });
  
  leftAmount = leftAmount.plus(getLastMonthInterest(mappedInstallments));
  loan.payments.filter(p => p.status === "ASSIGNED_TO_LOAN").forEach(payment => {
    realPaid = realPaid.plus(bigjs(payment.amount));
  })

  penaltyFees.filter(p => p.storno == null).forEach(penaltyFee => {
    leftAmount = leftAmount.plus(penaltyFee.unpaidAmount)
    let penalty = bigjs(penaltyFee.amount)
    let penaltyPaid = penalty.minus(penaltyFee.unpaidAmount)
    realPaid = realPaid.minus(penaltyPaid)
  })
    
  if (shouldPaid.lt(realPaid)) {
    leftAmount = leftAmount.minus(shouldPaid);
  } else {
    leftAmount = leftAmount.minus(realPaid);
  }

  return leftAmount.toFixed(2);
}

/**
 * getLastMonthInterest - get interest of last month
 *
 * @param {number} installments        Installments of loan
 */
export function getLastMonthInterest(installments) {
  const today = new Date();
  const paidInstallments = installments.filter( item => item.paymentDue <= today );
  const unpaidInstallment = installments.filter( item => item.paymentDue > today )[0];
  const lastPaidInstallment = paidInstallments[paidInstallments.length-1];
  const difDays = bigjs(today.getTime()).minus(bigjs(lastPaidInstallment.paymentDue.getTime())).div(bigjs(1000).times(3600).times(24)).round(0,3);
  if (unpaidInstallment) {
    let interest = bigjs(unpaidInstallment.interestAmount).div(bigjs(30)).times(difDays);
    return interest;
  } else
    return bigjs(0);
}



// WEBPACK FOOTER //
// ./src/common/utils/mathHelpers.js